import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@betareader/design-system';
import styled from 'styled-components';
import {
  CritiqueGroup,
  CritiqueJoinRequest,
  useResolveJoinRequest,
} from 'src/hooks/useCritiqueGroups';
import {
  activeMembers,
  nextCheckpoint,
  userRefId,
  userRefName,
} from 'src/utils/critiqueGroupUtil';
import { MediaQuery } from 'src/constants/size';
import { CritiqueAvatar } from 'src/components/critiqueGroups/critiqueAvatar';
import { MemberName } from 'src/components/critiqueGroups/memberName';
import { CritiqueEmptyState } from 'src/components/critiqueGroups/critiqueStates';
import {
  formatCheckpointDate,
  useDeliverableLabel,
} from 'src/components/critiqueGroups/checkpointTimeline';
import { CheckpointDonePulse } from './whosDone';

/** Design C1 — the host dashboard overview: next checkpoint, requests, roster. */

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 20px;
  ${MediaQuery.phone} {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div<{ $accent?: boolean }>`
  background: var(--br-surface);
  border: 1px solid ${({ $accent }) => ($accent ? 'var(--br-warning-border)' : 'var(--br-border)')};
  border-radius: 14px;
  padding: 18px;
`;

const CardTitle = styled.h4`
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 800;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  /* A name plus two actions doesn't fit a phone on one line — wrap instead of
     forcing the row (and so the page) wider than the viewport. */
  flex-wrap: wrap;
  & + & {
    border-top: 1px solid var(--br-border);
  }
`;

const RoleTag = styled.span<{ $host?: boolean }>`
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  color: ${({ $host }) => ($host ? 'var(--br-accent)' : 'var(--br-muted)')};
  background: var(--br-raised-surface);
`;

type Props = {
  group: CritiqueGroup;
  requests: {
    data?: CritiqueJoinRequest[];
    isLoading: boolean;
  };
  onOpenTab: (tab: any) => void;
};

const HostOverview: React.FC<Props> = ({ group, requests, onOpenTab }) => {
  const { t } = useTranslation();
  const resolve = useResolveJoinRequest(group._id);
  const deliverableLabel = useDeliverableLabel();
  const next = nextCheckpoint(group.checkpoints || []);
  const members = activeMembers(group);
  const pending = requests.data || [];

  return (
    <Grid data-testid="host-overview">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <CardTitle style={{ margin: 0 }}>
              {t('CritiqueNextCheckpoint', 'Next checkpoint')}
            </CardTitle>
            {next && (
              <Button
                size="small"
                variant="secondary"
                onClick={() => onOpenTab('checkpoints')}
                style={{ borderRadius: 7 }}
              >
                {t('CritiqueViewAll', 'View all')}
              </Button>
            )}
          </div>
          {next ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 12 }}>
              <CheckpointDonePulse groupId={group._id} checkpointId={next._id} size={58} />
              <div>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{next.label}</div>
                <div style={{ fontSize: 12.5, color: '#8a857b', marginTop: 2 }}>
                  {deliverableLabel(next)} · {formatCheckpointDate(next.dueAt)}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#8a857b', padding: '10px 0' }}>
              {t(
                'CritiqueNoCheckpointsYet',
                'No checkpoints yet — add one to give the circle a shared pace.'
              )}
              <div style={{ marginTop: 10 }}>
                <Button
                  size="small"
                  onClick={() => onOpenTab('checkpoints')}
                  style={{ borderRadius: 7, fontWeight: 700 }}
                  data-testid="overview-add-checkpoint"
                >
                  <span aria-hidden="true">＋ </span>
                  {t('CritiqueAddCheckpoint', 'Add checkpoint')}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {pending.length > 0 && (
          <Card $accent data-testid="overview-pending-requests">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span aria-hidden="true" style={{ color: 'var(--br-warning-text)' }}>＋</span>
              <CardTitle style={{ margin: 0, color: '#c85f14' }}>
                {t('CritiqueNJoinRequests', '{{count}} join requests', {
                  count: pending.length,
                })}
              </CardTitle>
            </div>
            {pending.map(r => (
              <Row key={r._id}>
                <CritiqueAvatar id={userRefId(r.user)} name={userRefName(r.user)} />
                <MemberName>{userRefName(r.user, t)}</MemberName>
                <Button
                  size="small"
                  style={{ borderRadius: 7 }}
                  disabled={resolve.isPending}
                  onClick={() => resolve.mutate({ requestId: r._id, action: 'approve' })}
                  data-testid={`approve-request-${r._id}`}
                >
                  {t('Approve', 'Approve')}
                </Button>
                <Button
                  size="small"
                  variant="secondary"
                  style={{ borderRadius: 7 }}
                  disabled={resolve.isPending}
                  onClick={() => resolve.mutate({ requestId: r._id, action: 'decline' })}
                >
                  {t('Decline', 'Decline')}
                </Button>
              </Row>
            ))}
          </Card>
        )}

        <Card>
          <CardTitle>
            {t('CritiqueMembersN', 'Members · {{count}}', { count: members.length })}
          </CardTitle>
          {members.length === 0 ? (
            <CritiqueEmptyState
              icon="user plus"
              title={t('CritiqueNoMembersTitle', 'Invite your first readers')}
              body={t(
                'CritiqueNoMembersBody',
                'Share an invite link or pick from readers who’ve followed your book.'
              )}
            />
          ) : (
            <>
              <Row>
                <CritiqueAvatar id={group.createdBy} name={t('You', 'You')} size={28} />
                <MemberName>{t('CritiqueYouHost', 'You')}</MemberName>
                <RoleTag $host>{t('CritiqueHost', 'Host')}</RoleTag>
              </Row>
              {members.map(m => (
                <Row key={m._id}>
                  <CritiqueAvatar
                    id={userRefId(m.user)}
                    name={userRefName(m.user)}
                    size={28}
                  />
                  <MemberName>{userRefName(m.user, t)}</MemberName>
                  <RoleTag>
                    {m.role === 'moderator'
                      ? t('CritiqueModerator', 'Moderator')
                      : t('CritiqueMember', 'Member')}
                  </RoleTag>
                </Row>
              ))}
              <div style={{ marginTop: 12 }}>
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => onOpenTab('members')}
                  style={{ borderRadius: 7, width: '100%' }}
                >
                  {t('CritiqueManageMembers', 'Manage members')}
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </Grid>
  );
};

export default HostOverview;
